import { cn } from "@/lib/utils";
import { Flag, ShieldCheck, MessageSquareCheck, Users, CheckCircle2, UserPlus, Network } from "lucide-react";
import { format } from "date-fns";

export type Milestone = {
  key: string;
  label: string;
  hint?: string;
  reached: boolean;
  at?: string | null;
  accent: "plum" | "ember" | "moss" | "sun" | "tide";
  icon: typeof Flag;
};

const ACCENT: Record<Milestone["accent"], { dot: string; ring: string; text: string }> = {
  plum: { dot: "bg-foreground", ring: "ring-foreground/20", text: "text-foreground" },
  ember: { dot: "bg-ember", ring: "ring-ember/30", text: "text-ember" },
  moss: { dot: "bg-moss", ring: "ring-moss/30", text: "text-moss" },
  sun: { dot: "bg-sun", ring: "ring-sun/30", text: "text-sun" },
  tide: { dot: "bg-tide", ring: "ring-tide/30", text: "text-tide" },
};

export function buildJourney(input: {
  joinedAt: string;
  issuesReported: number;
  verificationsGiven: number;
  acceptedSolutions: number;
  projects: number;
  issuesResolved: number;
  spaces: number;
}): Milestone[] {
  return [
    { key: "joined", label: "Joined Xamseen", hint: "The start of the journey", reached: true, at: input.joinedAt, accent: "plum", icon: UserPlus },
    { key: "space", label: "Joined a Space", hint: "Found a community", reached: input.spaces > 0, accent: "tide", icon: Users },
    { key: "report", label: "First report", hint: "Saw something, said something", reached: input.issuesReported > 0, accent: "ember", icon: Flag },
    { key: "verify", label: "First verification", hint: "Confirmed a neighbour's report", reached: input.verificationsGiven > 0, accent: "sun", icon: ShieldCheck },
    { key: "solution", label: "Supported a solution", hint: "Turned talk into an answer", reached: input.acceptedSolutions > 0, accent: "plum", icon: MessageSquareCheck },
    { key: "project", label: "Joined a project", hint: "Showed up for the work", reached: input.projects > 0, accent: "tide", icon: Network },
    { key: "resolved", label: "Helped resolve an issue", hint: "Real-world change", reached: input.issuesResolved > 0, accent: "moss", icon: CheckCircle2 },
  ];
}

export function JourneyTimeline({ milestones, className }: { milestones: Milestone[]; className?: string }) {
  const lastReached = milestones.reduce((acc, m, i) => (m.reached ? i : acc), 0);
  const progress = milestones.length > 1 ? (lastReached / (milestones.length - 1)) * 100 : 0;

  return (
    <div className={cn("relative", className)}>
      {/* connected path */}
      <span className="absolute left-[15px] top-3 bottom-3 w-px bg-border" aria-hidden />
      <span
        className="absolute left-[15px] top-3 w-px bg-foreground/70 transition-[height] duration-700"
        style={{ height: `calc(${progress}% - 0px)` }}
        aria-hidden
      />
      <ol className="space-y-4">
        {milestones.map((m, i) => {
          const a = ACCENT[m.accent];
          const Icon = m.icon;
          return (
            <li
              key={m.key}
              className="rise-in relative flex items-start gap-3"
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <span
                className={cn(
                  "relative z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full border transition-colors",
                  m.reached
                    ? cn("border-transparent ring-4", a.dot, a.ring)
                    : "border-border bg-surface"
                )}
              >
                <Icon className={cn("h-4 w-4", m.reached ? "text-background" : "text-muted-foreground")} />
              </span>
              <div className="min-w-0 flex-1 pt-1">
                <p className={cn("text-sm font-semibold", !m.reached && "text-muted-foreground")}>{m.label}</p>
                <p className="text-xs text-muted-foreground">
                  {m.reached
                    ? m.at ? format(new Date(m.at), "d MMM yyyy") : (m.hint ?? "Reached")
                    : `Not yet · ${m.hint ?? ""}`}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
